import React, {Fragment, FunctionComponent, useState} from "react";
import {PageTemplate} from "../../common/page-template/PageTemplate";
import {useTranslation} from "react-i18next";
import {Alert, Box} from "@mui/material";
import {useAppDispatch, useAppSelector} from "../../../store/AppStore";
import {MetadataCategoryCode} from "../../../book/MetadataCategoryCode";
import {ButtonsBox} from "./ButtonsBox";
import {
    Product,
    useDeleteApiV1ProductMutation,
    useGetApiV1UserMeQuery,
    usePostApiV1ProductExportMutation,
    usePostApiV1ProductMutation
} from "../../../api/GovernanceToolApi";
import {TableSkeleton} from "../../common/skeleton/TableSkeleton";
import {UserRole, UserRoleUtils} from "../../../book/UserRoleUtils";
import {ProductsTable} from "./ProductsTable";
import {Filters} from "./Filters";
import {DeleteProductDialog} from "./DeleteProductDialog";
import {
    ProcessStatusNotification,
    toProcessStatus
} from "../../common/process-status-notification/ProcessStatusNotification";
import {SearchInfo} from "../../common/search-info-grid/SearchInfo";
import {updateLastSearchResultAction} from "../../../action/UpdateLastSearchResultAction";

export const ProductSearch: FunctionComponent = () => {
    const {t} = useTranslation();
    const {
        filterSearch,
        filterReleaseType,
        filterProductProperties,
        products,
        count
    } = useAppSelector(state => (state.productSearchState));
    const dispatch = useAppDispatch();
    const [state, setState] = useState<ProductSearchState>({
        currentPage: 0,
        pageSize: 50,
    });
    const {data: userMe} = useGetApiV1UserMeQuery(undefined);
    const [searchProducts, {
        isLoading: isProductsLoading,
        error: productsError,
        fulfilledTimeStamp,
        startedTimeStamp,
        reset: resetProductSearch
    }] = usePostApiV1ProductMutation();
    const [exportProducts, {
        isLoading: isExport,
    }] = usePostApiV1ProductExportMutation();
    const [deleteProduct, {
        data: deleteProductData,
        isLoading: isDeleteProductLoading,
        error: deleteProductError,
        reset: resetDeleteProduct
    }] = useDeleteApiV1ProductMutation();

    const searchResultProducts = products ?? [];
    const searchResultCount = count ?? 0;

    const callSearchProducts = (page?: number, pageSize?: number) => {
        searchProducts({
            searchProductsRequest: {
                search: filterSearch.length ? filterSearch : null,
                productPropertyFilterIds: Object.values(filterProductProperties).map(p => p.id),
                categories: [...Object.values(MetadataCategoryCode), 'project configuration'],
                releaseType: filterReleaseType,
                page: page ?? state.currentPage,
                pageSize: pageSize ?? state.pageSize
            }
        }).then(result => {
            if ('data' in result) {
                dispatch(updateLastSearchResultAction(result.data.payload?.products, result.data.payload?.count))
            }
            return result;
        });
    }

    const ProductSearchInfoGrid = () => <SearchInfo
        showExecutionTime={UserRoleUtils.ToRole(userMe?.payload?.role) === UserRole.Admin}
        executionTime={(fulfilledTimeStamp ?? 0) - (startedTimeStamp ?? 0)}
        count={searchResultCount}
        currentPage={state.currentPage}
        pageSize={state.pageSize}
        onPageChange={page => {
            setState(state => ({...state, currentPage: page}));
            callSearchProducts(page);
        }}
        onPageSizeChange={pageSize => {
            setState(state => ({...state, pageSize}));
            callSearchProducts(undefined, pageSize);
        }}
        paginationLabel={t("Products per page")}
        itemsLabel={t("products")}/>;

    return <PageTemplate pageTitle={t("Search products")}>
        <Filters callSearchProducts={callSearchProducts}/>

        <ButtonsBox callSearchProducts={callSearchProducts}
                    showExportButton={Boolean(searchResultProducts.length)}
                    onReset={() => {
                        resetProductSearch();
                    }}
                    onExport={() => {
                        exportProducts({
                            exportRequest: {
                                search: filterSearch.length ? filterSearch : null,
                                productPropertyFilterIds: Object.values(filterProductProperties).map(p => p.id),
                                releaseType: filterReleaseType,
                            }
                        }).then(result => {
                            if ('data' in result) {
                                const url = window.URL.createObjectURL(result.data as Blob);
                                const hiddenHypertextLink = document.createElement('a');
                                hiddenHypertextLink.href = url;
                                hiddenHypertextLink.download = "export.csv";
                                document.body.appendChild(hiddenHypertextLink);
                                hiddenHypertextLink.click();
                                hiddenHypertextLink.remove();
                            }
                        })
                    }}
                    isExporting={isExport}/>

        {isProductsLoading &&
            <TableSkeleton sx={{marginTop: '40px'}}
                           colCount={8}
                           colCountMd={7}
                           colCountSm={6}
                           colCountXs={3}
                           rowCount={10}
                           showPagination/>}

        {Boolean(productsError) && <Alert severity="error" sx={{marginTop: '40px'}}>
            {t("An error occurred while searching products. Please contact the administrator or try again.")}
        </Alert>}

        {Boolean(!searchResultProducts.length) && Boolean(products) &&
            <Box>
                <Alert severity="warning" sx={{marginTop: '40px'}}>
                    {t("The search did not match any products")}
                </Alert>
            </Box>}

        {Boolean(searchResultProducts.length) &&
            <Fragment>
                <ProductSearchInfoGrid/>
                <ProductsTable products={searchResultProducts}
                               showDeleteProductButton={UserRoleUtils.ToRole(userMe?.payload?.role) === UserRole.Admin}
                               onDelete={(productId) => {
                                   setState(state => ({
                                       ...state,
                                       productToDelete: productId
                                   }));
                               }}/>
                <ProductSearchInfoGrid/>
            </Fragment>}

        {state.productToDelete &&
            <DeleteProductDialog product={state.productToDelete}
                                 onCancelClick={() => {
                                     setState(state => ({
                                         ...state,
                                         productToDelete: undefined
                                     }));
                                 }}
                                 onOkClick={() => {
                                     if (state.productToDelete) {
                                         deleteProduct({
                                             deleteProductRequest: {
                                                 productId: state.productToDelete.id
                                             }
                                         }).then(() => {
                                             if (state.currentPage !== 0) {
                                                 setState(state => ({...state, currentPage: 0}));
                                             } else {
                                                 callSearchProducts(0);
                                             }

                                             resetDeleteProduct();
                                         })
                                         setState(state => ({
                                             ...state,
                                             productToDelete: undefined
                                         }));
                                     }
                                 }}/>}

        <ProcessStatusNotification
            status={toProcessStatus(isDeleteProductLoading, Boolean(deleteProductData?.payload), Boolean(deleteProductError))}
            onClose={() => resetDeleteProduct()}
            onLoadingMessage={t("Deleting product please wait…")}
            onSuccessMessage={t("Product deleted successfully")}
            onErrorMessage={t("Product deleting failed")}/>
    </PageTemplate>;
}

interface ProductSearchState {
    currentPage: number;
    pageSize: number;
    productToDelete?: Product;
}